import { Injectable } from '@angular/core';
import { TranslationService } from '@ng-omar/translation';

export interface DateRange {
    start: Date;
    end: Date;
}

@Injectable()
export class GanttDateService {
    public constructor(
        private readonly translationService: TranslationService
    ) {}

    /**
     * Parses a date input to UTC Date
     */
    public parseUtcDate(dateInput: string | Date): Date | null {
        if (!dateInput) return null;

        if (typeof dateInput === 'string') {
            return new Date(dateInput);
        } else {
            return new Date(dateInput.getTime());
        }
    }

    /**
     * Adds days to a date and returns ISO string
     */
    public addDays(dateInput: string | Date, days: number): string {
        if (!dateInput) return '';

        let date: Date;
        if (typeof dateInput === 'string') {
            date = new Date(dateInput);
        } else {
            date = new Date(dateInput.getTime());
        }

        date.setDate(date.getDate() + days);
        return date.toISOString();
    }

    /**
     * Formats date by app direction:
     * - RTL: yyyy-MM-dd
     * - LTR: dd-MM-yyyy
     */
    public formatDate(dateInput: string | Date): string {
        if (!dateInput) return '';

        try {
            const date = this.parseUtcDate(dateInput);
            if (!date) return '';

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            return this.isRtl()
                ? `${year}-${month}-${day}`
                : `${day}-${month}-${year}`;
        } catch {
            return '';
        }
    }

    /**
     * Formats dates for gantt tooltips:
     * - LTR: MMM,d,yyyy (e.g. Jan,5,2026)
     * - RTL: yyyy,d,MMM (e.g. 2026,5,Jan)
     */
    public formatTooltipDate(dateInput: string | Date): string {
        if (!dateInput) return '';

        try {
            const date = this.parseUtcDate(dateInput);
            if (!date) return '';

            const formatter = new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
            const parts = formatter.formatToParts(date);
            const month =
                parts.find(part => part.type === 'month')?.value ?? '';
            const day = parts.find(part => part.type === 'day')?.value ?? '';
            const year = parts.find(part => part.type === 'year')?.value ?? '';

            if (!month || !day || !year) return '';

            const formattedDate = this.isRtl()
                ? `${year},${day},${month}`
                : `${month},${day},${year}`;

            return this.isRtl()
                ? `\u2066${formattedDate}\u2069`
                : formattedDate;
        } catch {
            return '';
        }
    }

    /**
     * Calculates the first day of the month for a given date
     */
    public getFirstDayOfMonth(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    /**
     * Calculates the last day of the month for a given date
     */
    public getLastDayOfMonth(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    }

    /**
     * Calculates the difference in days between two dates
     */
    public getDaysDifference(startDate: Date, endDate: Date): number {
        return Math.round(
            (endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)
        );
    }

    /**
     * Normalizes a date range to month boundaries
     */
    public normalizeToMonthBoundaries(start: Date, end: Date): DateRange {
        return {
            start: this.getFirstDayOfMonth(start),
            end: this.getLastDayOfMonth(end),
        };
    }

    /**
     * Calculates the number of months between two dates
     */
    public getMonthsDifference(startDate: Date, endDate: Date): number {
        return (
            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
            (endDate.getMonth() - startDate.getMonth()) +
            1
        );
    }

    private isRtl(): boolean {
        return this.translationService.currentLanguage?.isRtl ?? false;
    }
}
